import { createContext, useContext } from 'react';
import type { JSX, ReactNode } from 'react';
import type { FicheDAnnuaire } from '../views/contract.js';
import type { ReferenceDeclaree } from '../views/fields.js';

/**
 * LES PERSONNES DE L'ORGANISATION, cote SOCLE D'INTERFACE : le port, les
 * constantes, et la mise en annuaire. Rien d'autre.
 *
 * CE QUI FONDE CE FICHIER (chantier `champ-personne`). Un champ qui designe
 * quelqu'un (« Intervenant » d'une saisie d'heures, « Cree par ») stocke un
 * IDENTIFIANT STABLE, jamais une adresse tapee a la main : l'adresse change,
 * le nom change, l'identifiant reste. Deux identifiants coexistent, et ce
 * fichier les nomme tous les deux :
 *   - le `personId`, tire au sort a la creation de la fiche membre, que les
 *     fiches METIER stockent (un champ de type `personne`) ;
 *   - l'`uid` du compte, que le SDK pose dans `createdBy` / `updatedBy` (les
 *     rules l'exigent). Une personne invitee qui ne s'est jamais connectee
 *     n'en a pas encore.
 * L'annuaire des personnes (`orgs/{orgId}/personnes`) sert les deux, a partir
 * d'une seule lecture.
 *
 * POURQUOI UN PORT, comme l'acces (`lib/acces.tsx`) et les liens
 * (`lib/liens.tsx`) : `@taki/ui` ne parle jamais au SDK. L'hote lit
 * `sdk.personnes` et la fiche membre de la personne connectee, et remplit
 * cette forme. Sans port, rien ne casse : l'annuaire des personnes est marque
 * illisible avec sa raison (G16), le jeton de creation n'est pas resolu, et
 * aucune case ne montre d'identifiant (voir `REPLI_PERSONNE_INCONNUE`).
 */

/**
 * LA FAMILLE DE L'ANNUAIRE, telle qu'une reference la vise.
 *
 * ECRITE UNE SECONDE FOIS cote `@taki/data-model` (`packages/data-model/src/
 * personne.ts`), parce que ce package ne depend d'aucun modele de donnees (voir
 * l'en-tete de `views/fields.ts`). Un test de `@taki/app-runtime`, qui voit les
 * deux, les recoupe : un ecart ne passe pas la CI.
 */
export const FAMILLE_PERSONNES = 'personnes';

/**
 * LE SOUS-TYPE QUI FAIT DESIGNER UNE PERSONNE PAR SON UID, et non par son
 * `personId` : celui de `createdBy` / `updatedBy`. C'est un second ANNUAIRE
 * (`personnes/uid`, par `cleDeJeu`), servi par la meme lecture. Meme remarque
 * que `FAMILLE_PERSONNES` sur la copie cote data-model.
 */
export const TYPE_ANNUAIRE_PAR_UID = 'uid';

/** La reference implicite d'un champ `personne` : par `personId`. */
export const REFERENCE_PERSONNES: ReferenceDeclaree = Object.freeze({
  family: FAMILLE_PERSONNES,
});

/** La reference d'une trace (`createdBy`, `updatedBy`) : par uid. */
export const REFERENCE_PERSONNES_PAR_UID: ReferenceDeclaree = Object.freeze({
  family: FAMILLE_PERSONNES,
  type: TYPE_ANNUAIRE_PAR_UID,
});

/** Cette reference vise-t-elle l'annuaire des personnes, par `personId` ou par uid ? */
export function estReferenceDePersonnes(
  reference: { family: string } | null | undefined,
): boolean {
  return reference != null && reference.family === FAMILLE_PERSONNES;
}

/**
 * LE MOT MONTRE A LA PLACE D'UN IDENTIFIANT QUE L'ANNUAIRE NE CONNAIT PAS.
 *
 * UN IDENTIFIANT DE PERSONNE NE S'AFFICHE JAMAIS : ni le `personId` (vingt
 * caracteres tires au sort, qui ne disent rien), ni un uid. Une fiche creee par
 * un compte qui n'a plus de fiche membre, un script d'import, un compte
 * technique : la case dit ce mot, attenue, plutot qu'une chaine illisible.
 */
export const REPLI_PERSONNE_INCONNUE = 'Compte inconnu';

/** Les phrases des grisements, ecrites une fois. */
export const RAISON_PERSONNES = {
  /** Personne n'a monte de `PersonnesProvider` au-dessus de l'ecran. */
  sansPort: "L'hote ne fournit pas l'annuaire des personnes.",
} as const;

/** Une personne de l'annuaire, telle que le socle d'interface la lit (structurel). */
export interface PersonneVue {
  /** L'identifiant stable, celui que les fiches metier stockent. */
  personId: string;
  displayName: string;
  email: string;
  /** L'uid du compte ; `null` tant que la personne ne s'est jamais connectee. */
  userId: string | null;
  /** Derive de `userId`, jamais stocke : invitee sans compte, ou active. */
  statut: 'invite' | 'actif';
  /** La photo de son compte (https), posee par elle ; absente, l'ecran retombe sur les initiales. */
  photoURL?: string | null | undefined;
  /**
   * Retiree, desactivee ou supprimee : elle reste dans l'annuaire pour que les
   * fiches du passe gardent son NOM, mais elle ne se PROPOSE plus dans un choix.
   */
  archive: boolean;
}

/** Ce que l'hote fournit. */
export interface PersonnesPort {
  /** Tout l'annuaire, archives comprises : c'est la surface qui trie ce qu'elle propose. */
  lister(): Promise<PersonneVue[]>;
  /** La personne connectee, ou `null` (pas de fiche, fiche pas encore lue). */
  moi: PersonneVue | null;
  /** La fiche de la personne connectee est lue (ou son absence constatee). */
  pret: boolean;
}

const PersonnesContext = createContext<PersonnesPort | null>(null);

export function PersonnesProvider({
  value,
  children,
}: {
  value: PersonnesPort | null;
  children: ReactNode;
}): JSX.Element {
  return <PersonnesContext.Provider value={value}>{children}</PersonnesContext.Provider>;
}

/** Le port, ou `null` quand l'hote n'en fournit pas. */
export function usePersonnesPort(): PersonnesPort | null {
  return useContext(PersonnesContext);
}

/** Le nom d'abord, l'adresse a defaut : jamais l'identifiant. */
export function titreDePersonne(personne: Pick<PersonneVue, 'displayName' | 'email'>): string {
  const nom = personne.displayName.trim();
  return nom !== '' ? nom : personne.email;
}

/**
 * LA FICHE BRUTE D'UNE PERSONNE, telle qu'une reference la lit : c'est ce qui
 * laisse `reference.titre: 'socle:email'` choisir l'adresse comme titre, par
 * le meme chemin que le « Champ du nom » d'un lien (`lectureDeclaree`,
 * `views/screens.ts`). `id` est la clef de l'annuaire vise.
 */
function ficheBrute(personne: PersonneVue, id: string): Record<string, unknown> {
  return {
    id,
    personId: personne.personId,
    displayName: personne.displayName,
    email: personne.email,
    userId: personne.userId,
    archivedAt: personne.archive ? true : null,
  };
}

/**
 * L'ANNUAIRE DES PERSONNES, en fiches d'annuaire, par `personId` ou par uid.
 *
 * PAR UID, UNE INVITEE SANS COMPTE N'Y EST PAS : elle n'a encore rien cree ni
 * modifie, aucune trace ne peut la designer.
 *
 * LE TITRE est celui que la reference assemblee rend (`titleOf`, qui porte le
 * `titre` declare), sinon le nom, sinon l'adresse. LE SOUS-TITRE est
 * l'adresse quand le titre ne l'est pas deja : c'est ce qui distingue deux
 * homonymes dans un choix.
 *
 * FONCTION PURE ET EXPORTEE EXPRES : c'est la seule regle qui decide si un
 * identifiant devient un nom, et un test la tient sans React ni SDK.
 */
export function fichesDAnnuaireDePersonnes(
  personnes: readonly PersonneVue[],
  parUid: boolean,
  reference?: {
    titleOf?(fiche: Record<string, unknown>): string;
    subtitleOf?(fiche: Record<string, unknown>): string | undefined;
  },
): Map<string, FicheDAnnuaire> {
  const fiches = new Map<string, FicheDAnnuaire>();
  for (const personne of personnes) {
    const id = parUid ? personne.userId : personne.personId;
    if (id === null || id === '') continue;
    const brute = ficheBrute(personne, id);
    const declare = reference?.titleOf?.(brute);
    /* UN TITRE QUI RETOMBE SUR L'ID NE COMPTE PAS : c'est le repli de la
       reference quand rien ne la renseigne, et un identifiant ne s'affiche
       jamais (voir `REPLI_PERSONNE_INCONNUE`). */
    const title =
      declare !== undefined && declare.trim() !== '' && declare !== id
        ? declare
        : titreDePersonne(personne);
    const sousTitreDeclare = reference?.subtitleOf?.(brute);
    const subtitle =
      sousTitreDeclare !== undefined && sousTitreDeclare !== ''
        ? sousTitreDeclare
        : title !== personne.email && personne.email !== ''
          ? personne.email
          : undefined;
    fiches.set(id, {
      title,
      ...(subtitle === undefined ? {} : { subtitle }),
      ...(personne.photoURL == null || personne.photoURL === '' ? {} : { photo: personne.photoURL }),
      archive: personne.archive,
    });
  }
  return fiches;
}
